import type { FC } from "react";

import { Controller, useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs";
import { useTranslations } from "@ui/providers/useTranslations";
import { getPostCodeValidationRules } from "@ui/utils/getPostCodeValidationRules/getPostCodeValidationRules";

import type { PostCodeConfig } from "@jti/magento";

const defaultPostCodeConfig: PostCodeConfig = {
  postcode_validation_enabled: false,
  postcode_is_required: true,
  postcode_min_length: 0,
  postcode_max_length: 0,
  postcode_validate_regex: "",
  shouldHidePostCode: false,
  postCodeKeyboardType: "default",
};

type PostCodeProps = {
  fieldName?(name: string): string;
  config?: PostCodeConfig;
};

export const PostCode: FC<PostCodeProps> = ({ fieldName = (name) => name, config = defaultPostCodeConfig }) => {
  const { __ } = useTranslations();
  const { control } = useFormContext();

  if (config?.shouldHidePostCode) {
    return null;
  }

  const validationRules = getPostCodeValidationRules(config, __);
  const inputType = config.postCodeKeyboardType === "numeric" ? "number" : "text";

  const isPostCodeRequired = config.postcode_validation_enabled ? Boolean(config.postcode_is_required) : true;

  return (
    <Controller
      name={fieldName("postcode")}
      rules={validationRules}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextInput
          {...field}
          data-testid="postcode"
          placeholder={__("address_fields.postcode_placeholder")}
          label={__("address_fields.postcode")}
          labelId="label-address-postcode"
          required={isPostCodeRequired}
          isError={!!error}
          errorText={error?.message}
          type={inputType}
          autoComplete="postal-code"
        />
      )}
      defaultValue=""
    />
  );
};
